import { useState, useMemo } from 'react'
import { ArrowLeft, Loader2, Trash2, BookOpen } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/hooks/use-toast'
import {
  AdminUser,
  useUserEnrollments,
  useUnenrollUserFromCourse,
  UserEnrollment,
} from '@/services/admin/users.service'
import { EnrollUserDialog } from './EnrollUserDialog'
import { UserWatchedEpisodes } from './UserWatchedEpisodes'
import { UserWorkbookActivity } from './UserWorkbookActivity'
import { UserCourseProgress } from './UserCourseProgress'
import { UserQuizAttempts } from './UserQuizAttempts'
import { getErrorMessage } from '@/utils/error'

interface UserDetailsDialogProps {
  user: AdminUser
  open: boolean
  onOpenChange: (open: boolean) => void
}

const accessTypeLabels = {
  free: 'Gratuito',
  restricted: 'Restrito',
  paid: 'Pago',
}

export function UserDetailsDialog({ user, open, onOpenChange }: UserDetailsDialogProps) {
  const { toast } = useToast()
  const [unenrollingCourse, setUnenrollingCourse] = useState<UserEnrollment | null>(null)
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false)
  const loggedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}')
    } catch {
      return {}
    }
  }, [])
  const isSuperAdmin = loggedUser.role === 'super_admin'

  const { data: enrollments, isLoading } = useUserEnrollments(user.id)
  const unenrollMutation = useUnenrollUserFromCourse()

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const handleUnenroll = (enrollment: UserEnrollment) => {
    setUnenrollingCourse(enrollment)
  }

  const confirmUnenroll = async () => {
    if (!unenrollingCourse) return

    try {
      await unenrollMutation.mutateAsync({
        userId: user.id,
        courseId: unenrollingCourse.courseId,
      })

      toast({
        title: 'Matrícula removida',
        description: `${user.name} foi desmatriculado de "${unenrollingCourse.course.title}".`,
      })

      setUnenrollingCourse(null)
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: 'Erro ao desmatricular',
        description: getErrorMessage(error, 'Ocorreu um erro ao remover a matrícula.'),
      })
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onOpenChange(false)}
                className="mr-2"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              Detalhes do Usuário
            </DialogTitle>
            <DialogDescription>
              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={user.avatar} />
                    <AvatarFallback>{user.name.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-lg font-semibold text-foreground">👤 {user.name}</p>
                    <p className="text-sm text-muted-foreground">📧 {user.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">
                    📅 Membro desde:{' '}
                    <span className="text-foreground">{formatDate(user.createdAt)}</span>
                  </span>
                  <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                    🏷️ {user.role === 'admin' ? 'Administrador' : 'Usuário'}
                  </Badge>
                  <Badge variant={user.active ? 'default' : 'outline'}>
                    {user.active ? '✅ Ativo' : '🚫 Inativo'}
                  </Badge>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>

          <div className="border-t border-border/50 my-2" />

          <Tabs defaultValue="enrollments" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="enrollments">
                📚 Matrículas ({enrollments?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="progress">📊 Progresso</TabsTrigger>
              <TabsTrigger value="watched">🎬 Aulas Assistidas</TabsTrigger>
              <TabsTrigger value="workbooks">📄 Workbooks</TabsTrigger>
              <TabsTrigger value="quizzes">📝 Quizzes</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto mt-4">
              {/* Matrículas */}
              <TabsContent value="enrollments" className="mt-0">
                {/* Botão de matricular: apenas super_admin por enquanto (admin sem permissão em cursos globais) */}
                {isSuperAdmin && (
                  <div className="flex items-center justify-end mb-3">
                    <Button size="sm" onClick={() => setEnrollDialogOpen(true)}>
                      <BookOpen className="mr-2 h-4 w-4" />
                      Matricular em curso
                    </Button>
                  </div>
                )}

                <div className="border border-border/50 rounded-lg">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : enrollments && enrollments.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Curso</TableHead>
                          <TableHead>Matrícula</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {enrollments.map((enrollment) => (
                          <TableRow key={enrollment.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{enrollment.course.title}</p>
                                <p className="text-xs text-muted-foreground">
                                  {enrollment.enrolledBy ? '↳ Por: Admin' : '↳ Auto-matrícula'}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">{formatDate(enrollment.enrolledAt)}</span>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  enrollment.course.accessType === 'restricted'
                                    ? 'default'
                                    : 'secondary'
                                }
                              >
                                {enrollment.enrolledBy ? 'Manual' : 'Automática'}
                              </Badge>
                              <p className="text-xs text-muted-foreground mt-1">
                                (
                                {
                                  accessTypeLabels[
                                    enrollment.course.accessType as keyof typeof accessTypeLabels
                                  ]
                                }
                                )
                              </p>
                            </TableCell>
                            <TableCell>
                              {enrollment.completedAt ? (
                                <Badge variant="default">✅ Completo</Badge>
                              ) : enrollment.startedAt ? (
                                <Badge variant="secondary">📖 Em progresso</Badge>
                              ) : (
                                <Badge variant="outline">⏳ Não iniciado</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleUnenroll(enrollment)}
                                title="Desmatricular"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>📚 Nenhum curso matriculado</p>
                      <p className="text-sm mt-2">Clique em "Matricular em curso" para adicionar</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Progresso por Curso */}
              <TabsContent value="progress" className="mt-0">
                <UserCourseProgress userId={user.id} />
              </TabsContent>

              {/* Aulas Assistidas */}
              <TabsContent value="watched" className="mt-0">
                <div className="border border-border/50 rounded-lg">
                  <UserWatchedEpisodes userId={user.id} />
                </div>
              </TabsContent>

              {/* Workbooks */}
              <TabsContent value="workbooks" className="mt-0">
                <div className="border border-border/50 rounded-lg">
                  <UserWorkbookActivity userId={user.id} />
                </div>
              </TabsContent>

              {/* Quizzes */}
              <TabsContent value="quizzes" className="mt-0">
                <div className="border border-border/50 rounded-lg">
                  <UserQuizAttempts userId={user.id} />
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Confirm unenroll dialog */}
      <AlertDialog open={!!unenrollingCourse} onOpenChange={() => setUnenrollingCourse(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar desmatrícula</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja desmatricular <strong>{user.name}</strong> do curso{' '}
              <strong>{unenrollingCourse?.course.title}</strong>?
              <br />
              <br />O progresso do usuário será mantido caso ele se matricule novamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmUnenroll}
              className="bg-destructive hover:bg-destructive/90"
            >
              Desmatricular
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Enroll dialog */}
      <EnrollUserDialog user={user} open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen} />
    </>
  )
}
